// src/components/JobView/Tabs/TaskKanbanBoard.js
// Tablero Kanban genérico. Por defecto son los 3 carriles de ESTADO de una
// tarea (Pendiente -> En progreso -> Hecho), que es como nació; pasando `lanes`
// sirve cualquier juego de columnas — la Cola de Trabajo (S3) le pasa las
// máquinas de una estación. Usa react-beautiful-dnd con un wrapper compatible
// con React 18 StrictMode.
//
// CORRECCIÓN a D7 del HANDOFF-CENTRO-COMANDO-S0-S2: ese doc daba por hecho que
// `LANES` ya era parametrizable. NO lo era — era una const de módulo, con
// `grid-cols-3` fijo, y `handleDragEnd` DESCARTABA el drop dentro de la misma
// columna, así que reordenar vertical era imposible. Las dos cosas se arreglan
// aquí, en S3, y el uso viejo sigue funcionando sin tocarlo.
import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// react-beautiful-dnd no registra los Droppable bajo React 18 StrictMode
// (doble render en dev). Este wrapper difiere el montaje un frame para evitarlo.
const StrictModeDroppable = ({ children, ...props }) => {
    const [enabled, setEnabled] = useState(false);
    useEffect(() => {
        const raf = requestAnimationFrame(() => setEnabled(true));
        return () => { cancelAnimationFrame(raf); setEnabled(false); };
    }, []);
    if (!enabled) return null;
    return <Droppable {...props}>{children}</Droppable>;
};

// Carriles por defecto: los estados de una tarea. Se mantiene exportado porque
// TasksAndChecklistTab y sus tests lo importan.
export const LANES = [
    { key: 'pending', label: 'Pendiente', dot: 'bg-blue-400' },
    { key: 'in_progress', label: 'En progreso', dot: 'bg-yellow-400' },
    { key: 'completed', label: 'Hecho', dot: 'bg-green-500' },
];

// Normaliza cualquier estado al carril correspondiente.
export const normalizeStatus = (s) =>
    s === 'completed' ? 'completed' : s === 'in_progress' ? 'in_progress' : 'pending';

/**
 * @param {object[]} [lanes]   columnas: { key, label, dot?, badge? }. Por defecto, LANES.
 * @param {(id, key)=>string} [laneOf]  a qué columna va cada ficha. Por defecto, su estado.
 * @param {(id, laneKey, index)=>void} onMove  recibe TAMBIÉN el índice destino, para que
 *        quien lo use pueda persistir el orden. Se llama también cuando el drop
 *        es en la MISMA columna (reordenar), cosa que antes se descartaba.
 */
const TaskKanbanBoard = ({ title, icon, headerExtra, headerAction, tasks = [], onMove, renderCard, emptyText = 'Sin tareas.', lanes, laneOf, dragDisabled = false }) => {
    const carriles = lanes && lanes.length ? lanes : LANES;
    const carrilDe = laneOf || ((t) => normalizeStatus(t.status));

    const handleDragEnd = (result) => {
        const { draggableId, source, destination } = result;
        if (!destination) return;
        // Soltar exactamente donde estaba no es un movimiento.
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;
        onMove(draggableId, destination.droppableId, destination.index);
    };

    return (
        <div className="bg-white p-3 md:p-4 rounded-lg shadow flex flex-col md:h-full md:min-h-0">
            {/* Cabecera fija (no hace scroll) */}
            <div className="flex-shrink-0">
                <div className="flex items-center justify-between gap-2 mb-3 border-b pb-2">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">{icon} {title}</h3>
                    {headerAction}
                </div>
                {headerExtra}
            </div>
            <DragDropContext onDragEnd={handleDragEnd}>
                {/* Las columnas se reparten según cuántos carriles haya. En móvil
                    van en scroll horizontal: con 5 máquinas no caben apiladas. */}
                <div
                    className="grid grid-cols-1 gap-3 flex-grow min-h-0 overflow-x-auto"
                    style={{ gridTemplateColumns: `repeat(${carriles.length}, minmax(220px, 1fr))` }}
                >
                    {carriles.map((lane) => {
                        const laneTasks = tasks.filter((t) => carrilDe(t) === lane.key);
                        return (
                            <div key={lane.key} className="flex flex-col min-h-0">
                                <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-600 flex-shrink-0">
                                    {lane.dot && <span className={`w-2.5 h-2.5 rounded-full ${lane.dot}`} />}
                                    <span className="truncate" title={lane.label}>{lane.label}</span>
                                    <span className="text-xs text-gray-400">({laneTasks.length})</span>
                                    {lane.badge}
                                </div>
                                <StrictModeDroppable droppableId={lane.key}>
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`flex-grow md:overflow-y-auto rounded-md p-2 min-h-[110px] transition-colors ${snapshot.isDraggingOver ? 'bg-indigo-50 ring-2 ring-indigo-200' : 'bg-gray-50'}`}
                                        >
                                            {laneTasks.map((task, index) => (
                                                // dragDisabled: quien VE pero no puede MOVER (sin canManageQueue
                                // en la Cola) recibe el tablero de solo lectura.
                                <Draggable key={task.id} draggableId={String(task.id)} index={index} isDragDisabled={dragDisabled}>
                                                    {(prov, snap) => (
                                                        <div
                                                            ref={prov.innerRef}
                                                            {...prov.draggableProps}
                                                            {...prov.dragHandleProps}
                                                            className={`mb-2 ${dragDisabled ? '' : 'cursor-grab active:cursor-grabbing'} ${snap.isDragging ? 'opacity-95 rotate-1' : ''}`}
                                                        >
                                                            {renderCard(task)}
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                            {laneTasks.length === 0 && (
                                                <p className="text-xs text-gray-400 text-center py-3 select-none">{emptyText}</p>
                                            )}
                                        </div>
                                    )}
                                </StrictModeDroppable>
                            </div>
                        );
                    })}
                </div>
            </DragDropContext>
        </div>
    );
};

export default TaskKanbanBoard;
